import { createContext, useCallback, useContext, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { cx, sizeClass, type Hue } from './shared';

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
      <path d="M6.4 5 12 10.6 17.6 5 19 6.4 13.4 12 19 17.6 17.6 19 12 13.4 6.4 19 5 17.6 10.6 12 5 6.4z" />
    </svg>
  );
}

function CloseButton({ onClick, label = 'Dismiss' }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" className="btn btn--ghost btn--slate btn--icon btn--sm" aria-label={label} onClick={onClick}>
      <CloseIcon />
    </button>
  );
}

// ------------------------------------------------------------------ alert

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  hue?: Hue;
  /** soft (default), solid or outline. */
  variant?: 'soft' | 'solid' | 'outline';
  size?: 'sm' | 'base';
  icon?: ReactNode;
  title?: ReactNode;
  /** Extra controls on the right (e.g. a "Retry" button). */
  actions?: ReactNode;
  /** Shows a close button. */
  onDismiss?: () => void;
}

export function Alert({ hue, variant = 'soft', size, icon, title, actions, onDismiss, className, children, ...rest }: AlertProps) {
  const urgent = hue === 'red' || hue === 'imperial' || hue === 'amber' || hue === 'orange';
  return (
    <div
      role={urgent ? 'alert' : 'status'}
      className={cx('alert', variant !== 'soft' && `alert--${variant}`, hue && `alert--${hue}`, sizeClass('alert', size), className)}
      {...rest}
    >
      {icon && <span className="alert__icon">{icon}</span>}
      <div className="alert__content">
        {title && <strong className="alert__title">{title}</strong>}
        {children && <div className="alert__text">{children}</div>}
      </div>
      {(actions || onDismiss) && (
        <div className="alert__actions">
          {actions}
          {onDismiss && <CloseButton onClick={onDismiss} />}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- toasts

export interface ToastOptions {
  hue?: Hue;
  title?: ReactNode;
  text?: ReactNode;
  icon?: ReactNode;
  /** Milliseconds before it closes itself; 0 keeps it until dismissed. Default 5000. */
  duration?: number;
}

interface ToastEntry extends ToastOptions {
  id: number;
  leaving: boolean;
}

const ToastContext = createContext<((options: ToastOptions) => void) | null>(null);

/** Returns toast({ hue, title, text, duration }). Needs a <ToastProvider> above it. */
export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast() needs a <ToastProvider> above it.');
  return toast;
}

export type ToastPosition = 'bottom-right' | 'top-right' | 'top-left' | 'bottom-left' | 'top-center' | 'bottom-center';

export function ToastProvider({ position = 'bottom-right', children }: { position?: ToastPosition; children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((all) => all.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    // Remove after the exit animation (or at once with reduced motion).
    const delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 150;
    window.setTimeout(() => setToasts((all) => all.filter((t) => t.id !== id)), delay);
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nextId.current++;
      const duration = options.duration ?? 5000;
      setToasts((all) => [...all, { ...options, id, duration, leaving: false }]);
      if (duration > 0) window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div
        className={cx('toast-region', position !== 'bottom-right' && `toast-region--${position}`)}
        role="region"
        aria-label="Notifications"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={cx('toast', t.hue && `toast--${t.hue}`, t.leaving && 'is-leaving')}
            style={{ '--toast-duration': `${t.duration}ms` } as React.CSSProperties}
          >
            {t.icon && <span className="alert__icon">{t.icon}</span>}
            <div className="alert__content">
              {t.title && <strong className="alert__title">{t.title}</strong>}
              {t.text && <div className="alert__text">{t.text}</div>}
            </div>
            <div className="alert__actions">
              <CloseButton onClick={() => dismiss(t.id)} />
            </div>
            {!!t.duration && <span className="toast__progress" aria-hidden="true" />}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
